import type { Colony } from '../sim/Colony';
import type { AntCaste, AntSimWorld, ColonySnapshot } from '../sim/contracts';

type CasteKey = Exclude<AntCaste, 'queen'>;

interface SliderRef {
  key: CasteKey;
  row: HTMLDivElement;
  slider: HTMLInputElement;
  label: HTMLSpanElement;
  title: HTMLSpanElement;
}

interface PanelRefs {
  colony: Colony;
  root: HTMLDivElement;
  title: HTMLDivElement;
  queen: HTMLDivElement;
  stockpile: HTMLDivElement;
  eggs: HTMLDivElement;
  larvae: HTMLDivElement;
  sliders: SliderRef[];
  autoButton: HTMLButtonElement;
  autoMode: boolean;
}

const TEAM_META: Record<0 | 1, { name: string; dot: string }> = {
  0: { name: 'RED', dot: '#c94a2e' },
  1: { name: 'BLACK', dot: '#bac3cc' },
};

const CASTE_LABELS: Record<CasteKey, string> = {
  worker:  'Workers',
  soldier: 'Soldiers',
  nurse:   'Nurses',
  scout:   'Scouts',
  breeder: 'Breeders',
};

const CASTE_ORDER: CasteKey[] = ['worker', 'soldier', 'nurse', 'scout', 'breeder'];

const AUTO_RATIOS: Record<CasteKey, number> = {
  worker:  0.55,
  soldier: 0.15,
  nurse:   0.12,
  scout:   0.10,
  breeder: 0.08,
};

export class ColonyHUD {
  readonly dom: HTMLElement;
  private panels: PanelRefs[] = [];
  private readonly isMobile: boolean;
  private backdrop: HTMLDivElement | null = null;

  constructor(colonies: Colony[], private world: AntSimWorld) {
    this.isMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);

    const root = document.createElement('div');
    if (this.isMobile) {
      // iPhone / iPad: chart lives in a centred modal over a dimming backdrop.
      // Hidden until `toggle(true)` is called from the touch bar.
      const backdrop = document.createElement('div');
      backdrop.style.cssText = [
        'position:fixed', 'inset:0',
        'background:rgba(0,0,0,0.55)',
        'display:none',
        'z-index:11',
      ].join(';');
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.toggle(false);
      });
      this.backdrop = backdrop;
      document.body.appendChild(backdrop);

      root.style.cssText = [
        'position:fixed',
        'top:50%', 'left:50%',
        'transform:translate(-50%, -50%)',
        'width:min(92vw, 340px)',
        'max-height:80vh',
        'overflow-y:auto',
        'display:flex', 'flex-direction:column', 'gap:8px',
        'padding:10px',
        'background:rgba(15,20,25,0.95)',
        'border:1px solid rgba(255,255,255,0.08)',
        'border-radius:12px',
        'pointer-events:auto',
        'z-index:12',
        'font:12px/1.3 system-ui, sans-serif',
        'color:#eee',
        'display:none',
      ].join(';');
    } else {
      root.style.cssText = [
        'position:fixed',
        'right:8px',
        'bottom:8px',
        'width:240px',
        'display:flex',
        'flex-direction:column',
        'gap:8px',
        'pointer-events:auto',
        'z-index:5',
        'font:11px/1.3 system-ui, sans-serif',
        'color:#eee',
      ].join(';');
    }

    for (const colony of colonies) {
      this.panels.push(this.buildPanel(colony, root));
    }

    document.body.appendChild(root);
    this.dom = root;
  }

  /** Show/hide the modal. No-op on desktop — panel is always visible. */
  toggle(force?: boolean): void {
    if (!this.isMobile) return;
    const show = typeof force === 'boolean' ? force : this.dom.style.display === 'none';
    this.dom.style.display = show ? 'flex' : 'none';
    if (this.backdrop) this.backdrop.style.display = show ? 'block' : 'none';
  }

  private buildPanel(colony: Colony, parent: HTMLDivElement): PanelRefs {
    const meta = TEAM_META[colony.team];

    const card = document.createElement('div');
    card.style.cssText = [
      'background:rgba(15,20,25,0.82)',
      'border-radius:8px',
      'padding:8px 10px',
      'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
      'display:flex',
      'flex-direction:column',
      'gap:3px',
    ].join(';');

    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;cursor:pointer;';
    const title = document.createElement('div');
    title.style.cssText = `color:${meta.dot};font-weight:600;letter-spacing:0.5px;`;
    title.textContent = `\u25CF ${meta.name}`;
    const caret = document.createElement('span');
    caret.style.cssText = 'opacity:0.6;font-family:monospace;';
    caret.textContent = '▸';
    titleRow.appendChild(title);
    titleRow.appendChild(caret);

    const body = document.createElement('div');
    body.style.cssText = 'display:none;flex-direction:column;gap:3px;margin-top:4px;';

    // Clicking the title row toggles the body open/closed. Mobile already
    // lives inside a modal so we auto-expand there.
    const setOpen = (open: boolean) => {
      body.style.display = open ? 'flex' : 'none';
      caret.textContent = open ? '▾' : '▸';
    };
    titleRow.addEventListener('click', () => setOpen(body.style.display === 'none'));
    if (this.isMobile) setOpen(true);

    const queen = document.createElement('div');
    const stockpile = document.createElement('div');
    const eggs = document.createElement('div');
    const larvae = document.createElement('div');

    card.appendChild(titleRow);
    card.appendChild(body);
    body.appendChild(queen);
    body.appendChild(stockpile);
    body.appendChild(eggs);
    body.appendChild(larvae);

    const slidersWrap = document.createElement('div');
    slidersWrap.style.cssText = 'display:flex;flex-direction:column;gap:4px;margin-top:4px;';

    // Auto/Manual header.
    const autoRow = document.createElement('div');
    autoRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;font-size:10.5px;';
    const modeLabel = document.createElement('span');
    modeLabel.textContent = 'Caste mix — AUTO';
    modeLabel.style.cssText = 'opacity:0.85;letter-spacing:0.02em;';
    const autoButton = document.createElement('button');
    autoButton.type = 'button';
    autoButton.textContent = 'Reset';
    autoButton.style.cssText = 'font:10px system-ui,sans-serif;background:rgba(255,255,255,0.08);color:#eee;border:1px solid rgba(255,255,255,0.15);border-radius:4px;padding:2px 6px;cursor:pointer;';
    autoRow.appendChild(modeLabel);
    autoRow.appendChild(autoButton);
    slidersWrap.appendChild(autoRow);

    const refs: PanelRefs = {
      colony,
      root: card,
      title,
      queen,
      stockpile,
      eggs,
      larvae,
      sliders: [],
      autoButton,
      autoMode: true,
    };

    const sliders: SliderRef[] = [];
    for (const key of CASTE_ORDER) {
      sliders.push(this.buildSlider(colony, key, slidersWrap, refs, sliders));
    }
    refs.sliders = sliders;

    // Seed ratios to AUTO defaults at boot.
    this.applyAutoRatios(refs);

    autoButton.addEventListener('click', () => {
      refs.autoMode = true;
      this.applyAutoRatios(refs);
      modeLabel.textContent = 'Caste mix — AUTO';
    });

    const setManual = () => {
      if (!refs.autoMode) return;
      refs.autoMode = false;
      modeLabel.textContent = 'Caste mix — MANUAL';
    };
    for (const s of sliders) s.slider.addEventListener('input', setManual);

    body.appendChild(slidersWrap);
    parent.appendChild(card);

    return refs;
  }

  private applyAutoRatios(p: PanelRefs): void {
    for (const key of CASTE_ORDER) p.colony.ratios[key] = AUTO_RATIOS[key];
    for (const s of p.sliders) {
      const pct = Math.round((AUTO_RATIOS[s.key]) * 100);
      s.slider.value = String(pct);
      s.label.textContent = `${pct}%`;
    }
  }

  private buildSlider(
    colony: Colony,
    key: CasteKey,
    parent: HTMLDivElement,
    refs: PanelRefs,
    allSliders: SliderRef[],
  ): SliderRef {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;flex-direction:column;gap:1px;';

    const labelWrap = document.createElement('div');
    labelWrap.style.cssText = 'display:flex;justify-content:space-between;font-size:10.5px;';

    const title = document.createElement('span');
    title.textContent = `${CASTE_LABELS[key]} 0`;

    const label = document.createElement('span');
    label.style.cssText = 'opacity:0.85;';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.step = '1';
    slider.style.cssText = 'width:100%;margin:0;';

    const initPct = Math.round((colony.ratios[key] ?? 0) * 100);
    slider.value = String(initPct);
    label.textContent = `${initPct}%`;

    slider.addEventListener('input', () => {
      // Constrain: all caste sliders must sum to 100%. Dragging one
      // proportionally scales the others so the total stays fixed.
      const newPct = Number(slider.value);
      const others = allSliders.filter((s) => s !== refRef());
      const otherValues = others.map((s) => Number(s.slider.value));
      const otherSum = otherValues.reduce((a, b) => a + b, 0);
      const remaining = 100 - newPct;

      if (otherSum <= 0) {
        // Nobody else has a share — spread the remainder evenly.
        const each = Math.max(0, remaining / others.length);
        others.forEach((s) => {
          s.slider.value = String(Math.round(each));
          s.label.textContent = `${Math.round(each)}%`;
          colony.ratios[s.key] = each / 100;
        });
      } else {
        const scale = remaining / otherSum;
        others.forEach((s, i) => {
          const v = Math.max(0, otherValues[i] * scale);
          s.slider.value = String(Math.round(v));
          s.label.textContent = `${Math.round(v)}%`;
          colony.ratios[s.key] = v / 100;
        });
      }
      colony.ratios[key] = newPct / 100;
      label.textContent = `${newPct}%`;
    });

    // Resolve our own SliderRef reference lazily (built below).
    const refRef = () => allSliders.find((s) => s.key === key)!;
    void refs;

    labelWrap.appendChild(title);
    labelWrap.appendChild(label);
    row.appendChild(labelWrap);
    row.appendChild(slider);
    parent.appendChild(row);

    return { key, row, slider, label, title };
  }

  /** Auto-mode caste policy: adjusts `colony.ratios` each tick toward whatever
   *  the colony's current situation demands — no-queen → rebuild breeders,
   *  under attack → more soldiers, starving → more foragers + scouts, abundant
   *  food + safe → grow brood (nurses). Decouples auto from the player's
   *  whims; the health of the colony drives the mix. */
  private runAutoPolicy(p: PanelRefs, snap: ColonySnapshot): void {
    const alive = (snap.population.worker + snap.population.soldier +
                   snap.population.nurse + snap.population.scout +
                   snap.population.breeder) || 1;
    const foodPerAnt = snap.stockpile / alive;

    // Threat = opposing-team alive ants within 12 m of this colony's nest.
    const nest = p.colony.nest;
    let threat = 0;
    const r2 = 12 * 12;
    for (const a of this.world.ants) {
      if (!a.alive || a.team === p.colony.team) continue;
      const dx = a.x - nest.x;
      const dz = a.z - nest.z;
      if (dx * dx + dz * dz < r2) threat++;
    }

    let target: Record<CasteKey, number>;
    if (!snap.hasQueen) {
      // Rebuild at any cost: breed a new queen, skeleton-crew everyone else.
      target = { worker: 0.15, soldier: 0.10, nurse: 0.15, scout: 0.0, breeder: 0.60 };
    } else if (threat >= 3) {
      target = { worker: 0.25, soldier: 0.50, nurse: 0.10, scout: 0.10, breeder: 0.05 };
    } else if (foodPerAnt < 0.3) {
      target = { worker: 0.50, soldier: 0.10, nurse: 0.10, scout: 0.25, breeder: 0.05 };
    } else if (foodPerAnt > 2.0) {
      target = { worker: 0.35, soldier: 0.10, nurse: 0.35, scout: 0.10, breeder: 0.10 };
    } else {
      target = { ...AUTO_RATIOS };
    }

    // Smooth-blend to avoid UI flicker when stockpile hovers on a threshold.
    const BLEND = 0.1;
    for (const key of CASTE_ORDER) {
      const cur = p.colony.ratios[key];
      p.colony.ratios[key] = cur + (target[key] - cur) * BLEND;
    }
  }

  update(): void {
    for (const p of this.panels) {
      const snap: ColonySnapshot = p.colony.snapshot(this.world.ants);
      if (p.autoMode) this.runAutoPolicy(p, snap);
      if (snap.hasQueen) {
        p.queen.textContent = '\u265B QUEEN present';
        p.queen.style.color = '#eee';
      } else {
        p.queen.textContent = '\u265B QUEEN gone';
        p.queen.style.color = '#ff6461';
      }
      p.stockpile.textContent = `\uD83C\uDF4E ${Math.floor(snap.stockpile)}`;
      p.eggs.textContent = `\u25CF Eggs ${snap.eggs}`;
      p.larvae.textContent = `\u25CF Larvae ${snap.larvae}`;

      for (const s of p.sliders) {
        const pop = snap.population[s.key];
        s.title.textContent = `${CASTE_LABELS[s.key]} ${pop}`;
        // Only refresh slider DOM from the colony value if the user isn't dragging.
        if (document.activeElement !== s.slider) {
          const rawPct = Math.round((snap.ratios[s.key] ?? 0) * 100);
          if (s.slider.value !== String(rawPct)) {
            s.slider.value = String(rawPct);
          }
          s.label.textContent = `${rawPct}%`;
        } else {
          s.label.textContent = `${s.slider.value}%`;
        }
      }
    }
  }
}
